import axios, { AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { v4 } from "uuid";

import { getAuthToken } from "../utils";

import { withRateLimit } from "./rate-limiter";

console.log(`BASE URL`, import.meta.env.VITE_BASE_URL);

if (!import.meta.env.VITE_BASE_URL) {
  throw new Error(
    "VITE_BASE_URL is not defined. Please ensure the correct .env file is loaded for your environment (e.g., .env.prod, .env.dev.dev, .env.dev.local)."
  );
}

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  headers: {
    "Content-type": "application/json",
  },
});

apiClient.interceptors.request.use(function (config) {
  const token = getAuthToken();
  if (config.headers === undefined) {
    // @ts-ignore
    config.headers = {};
  }

  if (token) {
    config.headers.Authorization = token;
  }

  config.headers["x-ig-trace-id"] = v4();
  // Only send page URL on mutations (PUT/POST/PATCH/DELETE) for backend MP tracking.
  // Avoids sending navigation state on every GET request.
  if (config.method && !["get", "head", "options"].includes(config.method)) {
    try {
      config.headers["x-ig-page-url"] = encodeURI(window.location.href);
    } catch {
      config.headers["x-ig-page-url"] = window.location.href.replace(
        /[^\u0020-\u00FF]/g,
        ""
      );
    }
  }

  return config;
});

export const _request = async <Response, Data>(
  method: Method,
  url: string,
  config?: AxiosRequestConfig
) =>
  withRateLimit(url, async () =>
    apiClient.request<Response, AxiosResponse<Response>, Data>({
      method,
      url,
      ...config,
    })
  );

/** Build the get/post/... helpers, optionally pinned to a different origin via a
 * per-request `baseURL`. All calls still go through the one `apiClient` (auth
 * interceptor, rate limiter), so behavior stays 1-1 with `api`. */
const makeApi = (baseURL?: string) => ({
  get: async <Response, Data = any>(url: string, config?: AxiosRequestConfig) =>
    _request<Response, Data>("GET", url, { baseURL, ...config }),
  post: async <Response, Data = any>(
    url: string,
    data: Data,
    config?: AxiosRequestConfig
  ) => _request<Response, Data>("POST", url, { baseURL, data, ...config }),
  put: async <Response, Data = any>(
    url: string,
    data: Data,
    config?: AxiosRequestConfig
  ) => _request<Response, Data>("PUT", url, { baseURL, data, ...config }),
  patch: async <Response, Data = any>(
    url: string,
    data: Data,
    config?: AxiosRequestConfig
  ) => _request<Response, Data>("PATCH", url, { baseURL, data, ...config }),
  delete: async <Response, Data = any>(
    url: string,
    config?: AxiosRequestConfig
  ) => _request<Response, Data>("DELETE", url, { baseURL, ...config }),
});

export const api = makeApi();

/** Same surface as `api`, but every `/v2/analytics/*` call targets the analytics
 * origin. `igfe dev --analytics local` sets VITE_ANALYTICS_URL to a locally-run
 * node-backend (which proxies to a local analytics-v2), so only the slow
 * live-lambda analytics path is local while the rest of the app stays on
 * VITE_BASE_URL. Unset → baseURL is undefined → apiClient's VITE_BASE_URL wins. */
export const analyticsApi = makeApi(import.meta.env.VITE_ANALYTICS_URL);
